import { QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError, isPage, rows as toRows, type Page, type Query } from './api';
import { isPaginated } from './openapi';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
    },
    mutations: { retry: false },
  },
});

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const issues = e.details.issues as string[] | undefined;
    return issues?.length ? `${e.message}: ${issues.slice(0, 3).join('; ')}` : e.message;
  }
  return (e as Error)?.message ?? 'Something went wrong';
}

export function useApi<T = unknown>(path: string | null, query?: Query, options: Partial<UseQueryOptions<T>> = {}) {
  return useQuery<T>({
    queryKey: [path, query ?? {}],
    queryFn: ({ signal }) => api.get<T>(path!, { query, signal }),
    enabled: Boolean(path) && options.enabled !== false,
    ...options,
  });
}

/**
 * List any endpoint: cursor-paginated endpoints load page by page ("load more"),
 * plain arrays load at once. Returns flat rows either way.
 */
export function useList<T = Record<string, unknown>>(path: string | null, query: Query = {}, opts: { enabled?: boolean; limit?: number } = {}) {
  const paginated = path ? isPaginated(path.split('?')[0]) : false;
  const inf = useInfiniteQuery({
    queryKey: [path, 'list', query],
    queryFn: ({ pageParam, signal }) => api.get<unknown>(path!, { query: { ...query, limit: opts.limit ?? 25, cursor: pageParam as string | undefined }, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (isPage(last) ? (last as Page<T>).nextCursor ?? undefined : undefined),
    enabled: Boolean(path) && paginated && opts.enabled !== false,
  });
  const plain = useQuery({
    queryKey: [path, 'all', query],
    queryFn: ({ signal }) => api.get<unknown>(path!, { query, signal }),
    enabled: Boolean(path) && !paginated && opts.enabled !== false,
  });
  if (paginated) {
    return {
      rows: (inf.data?.pages ?? []).flatMap((p) => toRows<T>(p)),
      isLoading: inf.isLoading,
      isFetching: inf.isFetching,
      error: inf.error,
      hasMore: Boolean(inf.hasNextPage),
      loadMore: () => void inf.fetchNextPage(),
      loadingMore: inf.isFetchingNextPage,
      refetch: inf.refetch,
      raw: inf.data?.pages[0],
    };
  }
  return {
    rows: toRows<T>(plain.data),
    isLoading: plain.isLoading,
    isFetching: plain.isFetching,
    error: plain.error,
    hasMore: false,
    loadMore: () => undefined,
    loadingMore: false,
    refetch: plain.refetch,
    raw: plain.data,
  };
}

/** Mutation with success toast, error toast and cache invalidation of the given path prefixes. */
export function useAction<V = void, R = unknown>(fn: (v: V) => Promise<R>, opts: { success?: string | ((r: R) => string); invalidate?: string[]; onSuccess?(r: R): void; silentError?: boolean } = {}) {
  const qc = useQueryClient();
  return useMutation<R, unknown, V>({
    mutationFn: fn,
    onSuccess: (r) => {
      if (opts.success) toast.success(typeof opts.success === 'function' ? opts.success(r) : opts.success);
      for (const prefix of opts.invalidate ?? []) {
        void qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith(prefix) });
      }
      void qc.invalidateQueries({ queryKey: ['entity-list'] });
      opts.onSuccess?.(r);
    },
    onError: (e) => {
      if (!opts.silentError) toast.error(errorMessage(e));
    },
  });
}
